import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { workoutPlans } from '../data/workoutPlans';
import { getUserWorkoutPlans, getUserLastPlanUpdate, getUserWeeklyStrategy } from '../utils/userDataManager';
import { SkeletonList } from '../components/SkeletonLoader';
import './Schedule.css';

function Schedule() {
  const navigate = useNavigate();
  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const [weeklyPlans, setWeeklyPlans] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasCustomPlan, setHasCustomPlan] = useState(false);
  const [weeklyStrategy, setWeeklyStrategy] = useState(null);

  useEffect(() => {
    // Load custom plans if available
    const loadPlans = async () => {
      const customPlans = await getUserWorkoutPlans();
      const lastUpdate = await getUserLastPlanUpdate();
      const strategy = await getUserWeeklyStrategy();

      if (customPlans && lastUpdate) {
        setWeeklyPlans(customPlans);
        setHasCustomPlan(true);
        setWeeklyStrategy(strategy);
      } else {
        // No custom plan yet - use defaults as fallback but show prompt
        setWeeklyPlans(workoutPlans);
        setHasCustomPlan(false);
      }
      setLoading(false);
    };
    loadPlans();
  }, []);

  if (loading) {
    return (
      <div className="container">
        <div className="schedule-screen">
          <div className="schedule-header">
            <button className="back-button" onClick={() => navigate('/')}>← Back</button>
            <h2>Weekly Schedule</h2>
          </div>
          <SkeletonList count={7} />
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="schedule-screen">
        <div className="schedule-header">
          <button className="back-button" onClick={() => navigate('/')}>← Back</button>
          <h2>Weekly Schedule</h2>
        </div>

        {!hasCustomPlan && (
          <div className="no-plan-prompt">
            <div className="prompt-icon">🧠</div>
            <h3>No AI-Generated Plan Yet</h3>
            <p>You're currently viewing the default workout schedule. Let Claude design a personalized program based on your goals, fitness level, and schedule!</p>
            <button
              className="generate-plan-button"
              onClick={() => navigate('/weekly-planner')}
            >
              🚀 Generate My AI Workout Plan
            </button>
            <p className="default-schedule-label">Default Schedule (below):</p>
          </div>
        )}

        {hasCustomPlan && weeklyStrategy && (
          <div className="weekly-strategy-card">
            <div className="strategy-header">
              <span className="strategy-icon">🎯</span>
              <h3>This Week's Training Strategy</h3>
            </div>
            <div className="strategy-content">
              {weeklyStrategy.split('\n\n').map((paragraph, i) => (
                <p key={i}>{paragraph}</p>
              ))}
            </div>
          </div>
        )}

        {weeklyPlans && days.map((day, i) => {
          const workout = weeklyPlans[day];
          if (!workout || !workout.name) return null;
          return (
            <div key={day} className="day-card" onClick={() => navigate('/workout', { state: { workout, day } })}>
              <h3>{dayNames[i]}</h3>
              {workout.time && <p className="time">{workout.time}</p>}
              <h4>{workout.name}</h4>
              {workout.location && <p className="location">{workout.location}</p>}
              {workout.focus && <p className="focus">Focus: {workout.focus}</p>}

              {workout.coachingInsight && (
                <div className="coaching-insight">
                  <div className="insight-label">💬 Coach's Insight:</div>
                  <p className="insight-text">{workout.coachingInsight}</p>
                </div>
              )}

              {(workout.calorieGoal || workout.proteinGoal) && (
                <div className="schedule-nutrition">
                  {workout.calorieGoal && <span className="nutrition-badge">🔥 {workout.calorieGoal} cal</span>}
                  {workout.proteinGoal && <span className="nutrition-badge">🥩 {workout.proteinGoal}g protein</span>}
                </div>
              )}
              <p className="exercises">{workout.exercises.length} exercises</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default Schedule;
